import { useState } from 'react';
import { Box, Typography, Button, Snackbar, Alert } from '@mui/material';
import { Add, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { videosService } from '../services/videos.service';
import { Video } from '../types';
import { VideosTable, VideosToolbar } from '../components/videos/VideosTable';
import { VideosForm } from '../components/videos/VideosForm';

export default function VideosPage() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState<Video | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const queryClient = useQueryClient();

  const { data: videosData, isLoading } = useQuery({
    queryKey: ['videos', { page, limit, search }],
    queryFn: () => videosService.getList({ page, limit, search }),
  });

  const createMutation = useMutation({
    mutationFn: (data: Omit<Video, 'id' | 'createdAt' | 'updatedAt'>) => videosService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos'] });
      setIsFormOpen(false);
      setEditingVideo(null);
      setNotification({ message: 'Видео добавлено', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при создании', type: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Video> }) => videosService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos'] });
      setIsFormOpen(false);
      setEditingVideo(null);
      setNotification({ message: 'Видео обновлено', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при обновлении', type: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => videosService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos'] });
      setNotification({ message: 'Видео удалено', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при удалении', type: 'error' }),
  });

  const togglePublicMutation = useMutation({
    mutationFn: (id: number) => videosService.togglePublic(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos'] });
      setNotification({ message: 'Статус изменен', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка', type: 'error' }),
  });

  const filteredVideos = videosData?.data || [];
  const total = videosData?.pagination?.total || 0;

  const handleCreate = () => { setEditingVideo(null); setIsFormOpen(true); };
  const handleEdit = (video: Video) => { setEditingVideo(video); setIsFormOpen(true); };
  const handleSubmit = async (data: any) => {
    if (editingVideo) await updateMutation.mutateAsync({ id: editingVideo.id, data });
    else await createMutation.mutateAsync(data);
  };
  const handleDelete = (id: number) => { if (window.confirm('Удалить видео?')) deleteMutation.mutate(id); };
  const handleTogglePublic = (id: number) => togglePublicMutation.mutate(id);
  const handleCloseNotification = () => setNotification(null);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontFamily: 'Noto Serif' }}>Видео</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>Добавить видео</Button>
      </Box>
      <VideosToolbar search={search} onSearchChange={(value) => { setSearch(value); setPage(1); }} />
      <VideosTable videos={filteredVideos} loading={isLoading} total={total} page={page} limit={limit} onPageChange={setPage} onEdit={handleEdit} onDelete={handleDelete} onTogglePublic={handleTogglePublic} />
      <VideosForm open={isFormOpen} onClose={() => setIsFormOpen(false)} onSubmit={handleSubmit} initialData={editingVideo as any} loading={createMutation.isPending || updateMutation.isPending} error={createMutation.error?.message || updateMutation.error?.message} />
      <Snackbar open={!!notification} autoHideDuration={3000} onClose={handleCloseNotification} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleCloseNotification} severity={notification?.type === 'success' ? 'success' : 'error'} sx={{ width: '100%' }} icon={notification?.type === 'success' ? <CheckCircle /> : <ErrorIcon />}>{notification?.message}</Alert>
      </Snackbar>
    </Box>
  );
}
